import { useCallback } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';
import { captureAppException } from './errorCaptureUtils';

const BUCKET_NAME = 'event_images';
const MAX_SIZE_MB = 10; // Max file size in MB
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

interface UseMarkdownImageUploadProps {
  supabase: SupabaseClient;
  eventId: string; // Needed for creating the file path
}

interface UseMarkdownImageUploadReturn {
  imageUploadHandler: (image: File) => Promise<string>;
}

/**
 * Provides an imageUploadHandler for the MDXEditor imagePlugin,
 * uploading images directly to a Supabase Storage bucket.
 */
export const useMarkdownImageUpload = ({
  supabase,
  eventId,
}: UseMarkdownImageUploadProps): UseMarkdownImageUploadReturn => {
  const imageUploadHandler = useCallback(
    async (image: File): Promise<string> => {
      // 1. Check file size
      if (image.size > MAX_SIZE_BYTES) {
        const errorMsg = `Image size exceeds the ${MAX_SIZE_MB}MB limit.`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // 2. Generate a unique file path
      const fileExt = image.name.split('.').pop();
      const baseFileName = image.name.replace(/\.[^/.]+$/, "");
      const sanitizedBaseName = baseFileName.replace(/[^a-zA-Z0-9-_]/g, '_');
      const filePath = `public/${eventId}/${Date.now()}-${sanitizedBaseName}.${fileExt}`;

      try {
        // 3. Upload the file directly
        const { error: uploadError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(filePath, image, {
            cacheControl: '3600', // Example cache control
            upsert: false,
          });

        if (uploadError) {
          throw uploadError;
        }

        // 4. Get the public URL
        const { data: urlData } = supabase.storage
          .from(BUCKET_NAME)
          .getPublicUrl(filePath);

        if (!urlData || !urlData.publicUrl) {
          throw new Error('Failed to get public URL after upload.');
        }

        // 5. Return the public URL
        return urlData.publicUrl;

      } catch (error) {
        console.error('Error uploading image directly to Supabase:', error);
        captureAppException(error, {
          tags: {
            context: 'markdownImageUploadDirect', // Changed context tag
            eventId: eventId,
            bucket: BUCKET_NAME,
            filePath: filePath,
          },
          extra: {
            fileName: image.name,
            fileSize: image.size,
            fileType: image.type,
          },
        });

        throw new Error('Image upload failed. Please try again.');
      }
    },
    [supabase, eventId] // Dependencies include supabase now
  );

  return { imageUploadHandler };
}; 